import React from 'react';
import Konva from 'konva';
import {
  Stage as KonvaStage,
  StageProps,
  Layer,
  Line,
  Rect,
} from 'react-konva';
import CanvasStageListeners from 'components/canvas/components/canvas-stage-listeners';
import GesturesProvider from './components/gestures-provider';
import BulkSelectProvider from './components/bulk-select-provider';
import SelectionRectangle from './components/bulk-select-provider/components/selection-rectangle';
import {
  stageRefVar,
  layerRefVar,
  deleteConfirmModalRefVar,
} from 'components/canvas/hooks/use-canvas';
import { useCanvasStage, useCanvasStageOperations } from 'state/canvas/stage';
import {
  useLinkMenuPositionOperation,
  useSelectedLinkOperations,
} from 'state/canvas/selected-link';
import { useStageClickedOnOperations } from 'state/canvas/stage-clicked-on';
import useIsCanvasDraggable from 'components/canvas/hooks/use-is-canvas-draggable';
import useCanvasZoom from 'components/canvas/hooks/use-canvas-zoom';
import useIsKeyPressed from 'hooks/use-is-key-pressed';
import { DeleteConfirmModal, KonvaStageHolder } from './style';
import { throttle } from 'lodash';
import { isMobile } from 'react-device-detect';
import CanvasLoader from 'components/canvas/components/canvas-loader';
import SurfaceLinkContextMenu from 'routes/surface/components/surface-link-context-menu';
import RiskContextMenu from 'routes/surface/components/risk-context-menu';
import GlobalLinkZoneIndicator from 'components/canvas/components/global-link-zone-indicator';
import ClipboardZoneIndicator from 'components/canvas/components/clipboard-zone-indicator';
import IndicatorZoneMenu from 'routes/surface/components/indicator-zone-menu';
import useEventListener from 'hooks/use-event-listener';
import useWindowDimensions from 'components/canvas/hooks/use-window-dimensions';
import { ToolContext } from './components/paint-menu';

interface CanvasProps extends StageProps {
  id: string;
  children: any;
}

function Canvas({ id, children, ...props }: CanvasProps) {
  const toolContext = React.useContext(ToolContext);

  const deleteConfirmModal = deleteConfirmModalRefVar();

  const stageRef = stageRefVar();

  const layerRef = layerRefVar();

  const layerRef2 = layerRefVar();

  const stageInstance = stageRef.current?.getStage();

  const [isDraggable] = useIsCanvasDraggable();

  const { updateCanvasStage } = useCanvasStageOperations(id);

  useCanvasStage(id);

  const { width, height } = useWindowDimensions();

  // Is ctrl key pressed
  const isCtrlKeyPressed = useIsKeyPressed(17);

  const { updateCanvasStageInLocalStorage } = useCanvasStageOperations(id);

  const { setStageClickedOn } = useStageClickedOnOperations();

  const { clearLinkMenuPosition } = useLinkMenuPositionOperation();

  const { clearSelectedLink } = useSelectedLinkOperations();

  const { handlePinchZoom, handleWheelZoom } = useCanvasZoom(id);

  function onDragStart() {
    clearLinkMenuPosition();
    clearSelectedLink();
  }

  function onDragEnd({ target }) {
    if (target instanceof Konva.Stage && stageInstance) {
      updateCanvasStage({
        x: stageInstance?.x(),
        y: stageInstance?.y(),
      });
    }
  }

  function onMouseDown(event) {
    if (event.target instanceof Konva.Stage) {
      const container = event.target.getStage()?.container();
      if (container && isDraggable) {
        container.style.cursor = 'grabbing';
      }
    }
    // handleMouseDown(event);
  }

  function onMouseUp(event) {
    if (event.target instanceof Konva.Stage) {
      const container = event.target.getStage()?.container();
      if (container) {
        container.style.cursor = 'default';
      }
      // handleMouseUp(event);
    }
  }

  function onClick(event) {
    if (event.target instanceof Konva.Stage) {
      setStageClickedOn(new Date().toISOString());
    }
  }

  function onTouchEnd(event) {
    onDragEnd(event);
    onMouseUp(event);
  }

  function panCanvasOnWheel(event) {
    event.evt.preventDefault();

    if (isCtrlKeyPressed) {
      return handleWheelZoom(event.evt);
    }

    if (event.evt.ctrlKey) {
      // THIS is pinch https://medium.com/@auchenberg/detecting-multi-touch-trackpad-gestures-in-javascript-a2505babb10e
      return handlePinchZoom(event.evt);
    }

    const x = Math.abs(event.evt.deltaX) ? event.evt.deltaX : 0;

    const y = Math.abs(event.evt.deltaY) ? event.evt.deltaY : 0;

    if (stageInstance) {
      stageInstance?.move({ x, y });

      stageInstance?.draw();

      updateCanvasStageInLocalStorage(stageInstance?.position());
    }
  }

  // const [tool, setTool] = React.useState('pen');
  const tool = toolContext.selectedTool;
  const [lines, setLines] = React.useState([]);
  const isDrawing = React.useRef(false);

  const handleMouseDown = (event) => {
    isDrawing.current = true;

    // const pos = event.target.getStage().getPointerPosition();

    const stage = event.target.getStage();
    let pos = getRelativePointerPosition(stage);

    setLines([
      ...lines,
      { tool, points: [pos.x, pos.y], color: toolContext.selectedColor },
    ]);
    console.log(' HANDLE MOUSE DOWN', event.target.getStage(), pos);
  };

  function getRelativePointerPosition(node) {
    // the function will return pointer position relative to the passed node

    // get pointer (say mouse or touch) position
    var pos = node.getStage().getPointerPosition();

    // now we find relative point
    return transform?.point(pos);
    // return pos;
  }

  const handleMouseMove = (event) => {
    // no drawing - skipping
    if (!isDrawing.current) {
      return;
    }
    const stage = event.target.getStage();
    let point = getRelativePointerPosition(stage);
    // const point = stage.getPointerPosition();
    let lastLine = lines[lines.length - 1];
    // add point
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    // replace last
    lines.splice(lines.length - 1, 1, lastLine);
    // console.log(' HANDLE MOUSE MOVE', lastLine.points);
    setLines(lines.concat(lastLine));
  };

  React.useEffect(() => {
    console.log();
    if (lines.length > 0) {
      localStorage.setItem(`line-${id}`, JSON.stringify(lines));
    }
  }, [lines]);

  React.useEffect(() => {
    console.log('UPDATE LINES');
    let string = localStorage.getItem(`line-${id}`);
    if (string && string != '') {
      setLines(JSON.parse(string)); // JSON.stringify(lines));
    }
  }, [id]);

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  var transform = stageRef.current?.getAbsoluteTransform()?.copy();
  // to detect relative position we need to invert transform
  transform?.invert();

  // React.useEffect(() => {
  console.log(' LINES ', lines.length);
  // }, [lines]);

  return (
    <>
      <CanvasLoader />
      <DeleteConfirmModal ref={deleteConfirmModal} />
      <CanvasStageListeners surface={id} />
      <GesturesProvider onPinch={handlePinchZoom}>
        <BulkSelectProvider>
          <KonvaStageHolder>
            <KonvaStage
              {...props}
              ref={stageRef}
              width={width}
              height={height}
              {...(isMobile
                ? {
                    onTap: onClick,
                    onTouchStart: onMouseDown,
                    onTouchEnd: onTouchEnd,
                    onDragEnd,
                    onDragStart,
                  }
                : {
                    // onMouseDown: handleMouseDown,
                    // onMouseMove: handleMouseMove,
                    // onMouseUp: handleMouseUp,
                    // onDragEnd,
                    // onDragStart,
                    onMouseDown: tool == null ? onMouseDown : handleMouseDown,
                    onMouseUp: tool == null ? onMouseUp : handleMouseUp,
                    onMouseMove: tool == null ? null : handleMouseMove,
                    onDragEnd: tool == null ? onDragEnd : null,
                    onDragStart: tool == null ? onDragStart : null,
                    onClick,
                    onWheel: throttle(panCanvasOnWheel, 50),
                  })}
              draggable={isDraggable && tool == null}>
              <Layer ref={layerRef}>
                {children}

                <SelectionRectangle surface={id} />
              </Layer>

              <Layer ref={layerRef2}>
                {/* <Rect
                  x={0}
                  y={0}
                  width={500}
                  height={500}
                  stroke="red"
                  shadowBlur={0}
                /> */}
                {lines.map((line, index) => (
                  <Line
                    key={index}
                    points={line.points}
                    stroke={line.color}
                    strokeWidth={line.tool === 'erase' ? 40 : 5}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={
                      line.tool === 'erase' ? 'destination-out' : 'source-over'
                    }
                  />
                ))}
              </Layer>
            </KonvaStage>
          </KonvaStageHolder>
          <SurfaceLinkContextMenu />
          <RiskContextMenu />
          <IndicatorZoneMenu>
            <GlobalLinkZoneIndicator />
            <ClipboardZoneIndicator />
          </IndicatorZoneMenu>
        </BulkSelectProvider>
      </GesturesProvider>
    </>
  );
}

export default Canvas;
